import React from "react";
import { Link } from "react-router-dom";

/**
 * MinimalFooter — utility-style, premium-corporate footer used on pages
 * where the customer's focus should stay on the page's CTA (e.g. /plans).
 *
 * Design intent (vs the default Footer):
 *   - No top wave, no big brand block, no descriptive paragraph.
 *   - Single dark band, 3 thin rows (links · cities · legal+copy).
 *   - Drops total height from ~520px to ~200px on desktop.
 *
 * Inspired by Rhythm Energy's final footer: short, dark, factual.
 */

const PUCT_REP = "PUCT REP# Pending";
const PHONE = "(800) 715-6516";
const EMAIL = "support@velcorenergy.com";

const CITIES = [
  { slug: "houston",       label: "Houston" },
  { slug: "dallas",        label: "Dallas" },
  { slug: "san-antonio",   label: "San Antonio" },
  { slug: "fort-worth",    label: "Fort Worth" },
  { slug: "arlington",     label: "Arlington" },
  { slug: "corpus-christi", label: "Corpus Christi" },
];

export default function MinimalFooter({ flush = false }) {
  return (
    <footer
      className={`relative text-velcor-mint ${flush ? "" : "mt-24 md:mt-32"}`}
      style={{ background: "#0b1324" }}
      data-testid="minimal-footer"
    >
      <div className="max-w-[1180px] mx-auto px-6 pt-10 pb-8">
        {/* Row 1 — brand + contact (single line on desktop, stacked on mobile) */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pb-6 border-b border-white/8">
          <div className="flex items-center gap-3">
            <img
              src="/velcor-logo.png"
              alt="Velcor Energy"
              width={32}
              height={32}
              className="object-contain shrink-0"
              style={{ width: 32, height: 32 }}
            />
            <span className="font-black text-[14px] tracking-tight text-white">
              VELCOR ENERGY
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-[12.5px] text-velcor-mint/80">
            <a
              href={`tel:${PHONE.replace(/\D/g, "")}`}
              className="hover:text-white transition-colors tabular-nums font-semibold"
              data-testid="minimal-footer-phone"
            >
              {PHONE}
            </a>
            <span className="opacity-30">·</span>
            <a
              href={`mailto:${EMAIL}`}
              className="hover:text-white transition-colors font-semibold"
              data-testid="minimal-footer-email"
            >
              {EMAIL}
            </a>
          </div>
        </div>

        {/* Row 2 — cities (single inline list, no column header) */}
        <nav
          aria-label="Ciudades de servicio"
          className="py-5 border-b border-white/8 flex flex-wrap gap-x-5 gap-y-2 text-[12.5px] text-velcor-mint/70"
          data-testid="minimal-footer-cities"
        >
          {CITIES.map((c, i) => (
            <React.Fragment key={c.slug}>
              {i > 0 && <span aria-hidden className="opacity-25">·</span>}
              <Link
                to={`/electricidad-${c.slug}`}
                className="hover:text-white transition-colors font-semibold"
                data-testid={`minimal-footer-city-${c.slug}`}
              >
                {c.label}
              </Link>
            </React.Fragment>
          ))}
        </nav>

        {/* Row 3 — legal + copy */}
        <div className="pt-5 flex flex-col-reverse md:flex-row md:items-center md:justify-between gap-3 text-[11.5px] text-velcor-mint/55 leading-relaxed">
          <span data-testid="minimal-footer-copy">
            © {new Date().getFullYear()} Velcor Energy LLC · {PUCT_REP}
          </span>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 font-semibold">
            <Link
              to="/privacy"
              className="hover:text-white transition-colors"
              data-testid="minimal-footer-privacy"
            >
              Privacy
            </Link>
            <span aria-hidden className="opacity-25">·</span>
            <Link
              to="/terms"
              className="hover:text-white transition-colors"
              data-testid="minimal-footer-terms"
            >
              Terms
            </Link>
            <span aria-hidden className="opacity-25">·</span>
            <Link
              to="/your-rights"
              className="hover:text-white transition-colors"
              data-testid="minimal-footer-yrac"
            >
              YRAC
            </Link>
            <span aria-hidden className="opacity-25">·</span>
            <a
              href="https://www.puc.texas.gov/"
              target="_blank"
              rel="noreferrer"
              className="hover:text-white transition-colors"
              data-testid="minimal-footer-puct"
            >
              PUCT
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
